import React, { useState, useEffect, useMemo, Component } from "react";
import Select, { components } from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { FixedSizeList as List } from "react-window";
import {
  white,
  textPrimary,
  textSecondary,
  newBorder,
  primary,
  hoverItemColor,
  color8,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import { Flex, Box, Text, Spinner, Avatar, Stack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import StatusBadge from "../badges/StatusBadge";
import StatusBadgeNew from "../badges/StatusBadgeNew";
import dayjs from "dayjs";
import SearchInput from "../input/SearchInput";
import { listAllUsersEmployeesSimple } from "../../actions/userActions";
import { listEmployeesAllSimple } from "../../actions/employeeActions";
import { useParams } from "react-router-dom";
import useIsMobile from "../../hooks/useIsMobile";

const MenuList = (props) => {
  const { options, children, getValue, selectProps } = props;
  const isMobile = selectProps.isMobile;

  const height = isMobile ? 72 : 63; // <- Mobile height
  // console.log("height", height);

  const [value] = getValue();
  const initialOffset = options.indexOf(value) * height;

  if (options.length === 0) {
    return (
      <Flex
        align="center"
        justify="center"
        h="48px"
        borderBottom={`1px solid ${newBorder}`}
        color={textSecondary}
      >
        No Results Found
      </Flex>
    );
  }

  const itemCount = children.length;
  const calculatedHeight = itemCount * height;
  const maxHeight = 369;
  const finalHeight = Math.min(calculatedHeight, maxHeight);

  return (
    <List
      height={finalHeight}
      itemCount={itemCount}
      itemSize={height}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => (
        <Flex h="100%" style={style} borderBottom={`1px solid ${newBorder}`}>
          {children[index]}
        </Flex>
      )}
    </List>
  );
};

const EmployeeSelectNew = ({ onChange, setAddCoordinatorModalOpen }) => {
  const { slug } = useParams();
  const isMobile = useIsMobile();

  // TRANSLATION
  const { t } = useTranslation();

  // REDUX
  const dispatch = useDispatch();

  const employeeListSimple = useSelector((state) => state.employeeListSimple);
  const { employees, loading, error } = employeeListSimple;

  // STATE

  const [inputValue, setInputValue] = useState("");

  // HANDLERS
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // FILTERED
  const filteredOptions = useMemo(() => {
    // Normalize the input for case-insensitive comparison
    const normalizedInput = inputValue.trim().toLowerCase();

    const filtered = employees
      .filter((patient) => {
        // Concatenate and normalize patient's full name and patientID for comparison
        const fullName =
          `${patient.firstNameNormalized} ${patient.lastNameNormalized}`.toLowerCase();
        const patientID = patient.patientID
          ? patient.patientID.toLowerCase()
          : "";

        // Check if the normalized full name or patientID includes the normalized input
        return (
          !normalizedInput ||
          fullName.includes(normalizedInput) ||
          patientID.includes(normalizedInput)
        );
      })

      .map((patient) => ({
        value: patient._id,
        label:
          patient.firstName && patient.lastName
            ? `${patient.firstName} ${patient.lastName}`
            : "Loading...",
        firstName: patient.firstName,
        lastName: patient.lastName,
        patientID: patient.patientID,
        birthDate: patient.birthDate,
        profession: patient.profession,
        status: patient.status,
      }));

    // Optionally limit the number of results here if needed
    // return filtered.slice(Math.max(filtered.length - 50, 0));
    return filtered.slice(0, 50);
  }, [inputValue, employees]);

  // USE EFFECT
  useEffect(() => {
    dispatch(listEmployeesAllSimple(slug));
  }, [dispatch, slug]);

  // HANDLERS

  const handleChange = (selectedOption) => {
    if (onChange) {
      onChange(selectedOption);
    }

    setAddCoordinatorModalOpen(false);
  };

  // Custom rendering for options
  const formatOptionLabel = ({
    firstName,
    lastName,
    profession,
    birthDate,
    status,
  }) => (
    <Flex align="center" justify="space-between" w="100%" h="100%" p="0 0.5rem">
      <Flex align="center" h="100%">
        <Avatar
          h={["2rem", "1.5rem"]}
          w={["2rem", "1.5rem"]}
          fontSize="xs"
          name={`${lastName} ${firstName}`}
          // bg="#dceeff"
          // color="#3a74a9"
          bg={color8}
          color={white}
          mr="0.5rem"
        />
        <Stack gap={0}>
          <Text
            fontSize="md"
            fontWeight="500"
            color={textPrimary}
            maxW={["180px", "300px"]}
            isTruncated
          >
            {lastName}{" "}
            <Text as="span" fontWeight="400">
              {firstName}
            </Text>
          </Text>
          <Text
            fontSize="0.8rem"
            color={textSecondary}
            fontWeight="500"
            mr="0.5rem"
          >
            {profession && profession !== "null" ? profession.name : "-"}
          </Text>
        </Stack>
      </Flex>

      <Flex justify="flex-end" align="center">
        <StatusBadgeNew
          fontSize="xs"
          p="0.25rem 1rem"
          options="user"
          status={status}
          ml={2}
        />
      </Flex>

      {/* <StatusBadge isSmall options="user" status={status} ml={2} /> */}
    </Flex>
  );

  const CustomInput = () => null; // Render nothing for the default input

  // LOG
  // console.log("employees", employees);

  return (
    <>
      {loading ? (
        <Flex w="100%" align="center" justify="center" p="3rem 0">
          <Spinner size="lg" color={primary} />
        </Flex>
      ) : (
        <Box
          w="100%"
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: 0.2 }}
        >
          <Flex w="100%">
            <SearchInput
              placeholder={t("employee.search")}
              onChange={handleInputChange}
              searchName={inputValue}
              p={["0 1.5rem", "0"]}
            />
          </Flex>

          <Box w="100%" minH="369px">
            {isMobile && (
              <Text
                fontSize="0.875rem"
                fontWeight="500"
                color={textPrimary}
                p="1rem 1.5rem 0.5rem"
              >
                {t("employee.employees")}
              </Text>
            )}
            <Select
              autoFocus
              menuIsOpen
              isSearchable={false}
              isMobile={isMobile} // ✅ this is the key line
              components={{
                Input: CustomInput,
                MenuList,
              }}
              // components={{ MenuList }}
              options={filteredOptions}
              onInputChange={(newInputValue, { action }) => {
                if (action === "input-change") {
                  setInputValue(newInputValue);
                }
              }}
              onChange={handleChange}
              formatOptionLabel={formatOptionLabel}
              placeholder={t("placeholder.selectPatient")}
              filterOption={() => true} // Since you're manually filtering options, always return true here
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  // borderColor: isError
                  //   ? "red"
                  //   : state.isFocused
                  //   ? newBorder
                  //   : newBorder,
                  color: textPrimary,
                  display: "none",
                  borderRadius: "1rem",
                  backgroundColor: `${white}`,
                  border: `1px solid ${newBorder}`,
                  fontSize: "0.938rem",
                  fontWeight: "500",
                  height: "3rem",
                  padding: "0 0.25rem",
                  ":focus-visible": {
                    borderColor: primary,
                    boxShadow: `0 0 0 1px ${newBorder}`,
                  },
                  ":hover": {
                    cursor: "pointer",
                  },
                }),
                menu: (provided) => ({
                  ...provided,
                  position: "relative",
                  zIndex: 9999,
                  borderRadius: isMobile ? "0rem" : "1rem",
                  overflow: "hidden",
                  boxShadow: isMobile ? "none" : `0 0 0 1px ${newBorder}`,
                  borderTop: isMobile ? `1px solid ${newBorder}` : `none`,

                  height: "100%",
                }),
                menuList: (provided) => ({
                  ...provided,
                  borderRadius: "1rem",
                }),
                option: (provided, state) => ({
                  ...provided,
                  color: state.isSelected ? "white" : textSecondary, // Color of text in options
                  backgroundColor: state.isSelected ? primary : white, // Background color of selected option
                  ":hover": {
                    backgroundColor: state.isSelected
                      ? primary
                      : hoverItemColor, // Background color when hovering
                    // color: "white",
                    cursor: "pointer",
                  },
                }),
                singleValue: (provided, state) => {
                  const opacity = state.isDisabled ? 0.5 : 1;
                  const transition = "opacity 300ms";
                  const color =
                    state.data.value === "" ? "desired color" : "default color";

                  return { ...provided, opacity, transition, color };
                },
              }}
              noOptionsMessage={() => t("placeholder.noOptions")}
            />
            {filteredOptions.length > 49 && (
              <Flex w="100%" align="center" justify="center">
                <Text fontSize="xs" color={textSecondary} mt="1rem">
                  {t("employee.selectEmployeetNote")}
                </Text>
              </Flex>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default EmployeeSelectNew;
